import React, { createContext, PropsWithChildren, useState } from 'react';

type ContextType = {
  editUserDrawerUserId: string | null;
  openEditUserDrawer: (userId: string) => void;
  closeEditUserDrawer: () => void;
  isAddUserDrawerOpen: boolean;
  openAddUserDrawer: () => void;
  closeAddUserDrawer: () => void;
};

const UserManagementNavigationContext = createContext<ContextType>({
  editUserDrawerUserId: null,
  openEditUserDrawer: () => {},
  closeEditUserDrawer: () => {},
  isAddUserDrawerOpen: false,
  openAddUserDrawer: () => {},
  closeAddUserDrawer: () => {},
});

const getEditUserIdFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('userId');
};

const getIsAddUserFromUrl = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('addUser') === 'true';
};

export const UserManagementNavigationContextProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const [editUserDrawerUserId, setEditUserDrawerUserId] = useState<
    string | null
  >(() => getEditUserIdFromUrl());
  const [isAddUserDrawerOpen, setIsAddUserDrawerOpen] = useState<boolean>(() =>
    getIsAddUserFromUrl(),
  );

  const openEditUserDrawer = (userId: string) => {
    setEditUserDrawerUserId(userId);
    setIsAddUserDrawerOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.set('userId', userId);
    url.searchParams.delete('addUser');
    window.history.replaceState(null, '', url.toString());
  };

  const closeEditUserDrawer = () => {
    setEditUserDrawerUserId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('userId');
    window.history.replaceState(null, '', url.toString());
  };

  const openAddUserDrawer = () => {
    setIsAddUserDrawerOpen(true);
    setEditUserDrawerUserId(null);
    const url = new URL(window.location.href);
    url.searchParams.set('addUser', 'true');
    url.searchParams.delete('userId');
    window.history.replaceState(null, '', url.toString());
  };

  const closeAddUserDrawer = () => {
    setIsAddUserDrawerOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('addUser');
    window.history.replaceState(null, '', url.toString());
  };

  return (
    <UserManagementNavigationContext.Provider
      value={{
        editUserDrawerUserId,
        openEditUserDrawer,
        closeEditUserDrawer,
        isAddUserDrawerOpen,
        openAddUserDrawer,
        closeAddUserDrawer,
      }}
    >
      {children}
    </UserManagementNavigationContext.Provider>
  );
};

export const useUserManagementNavigationContext = () =>
  React.useContext(UserManagementNavigationContext);
